import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

/**
 * Regalos por compra: agrega cada regalo cuando el carrito llega a su meta y lo quita cuando baja.
 * Los datos vienen de snippets/cart-gift.liquid, que se vuelve a renderizar con el carrito.
 */
const ADDED_KEY = 'toolstore-gift-added';
const DECLINED_KEY = 'toolstore-gift-declined';

let busy = false;

const store = {
  get: (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key) => {
    try {
      sessionStorage.setItem(key, '1');
    } catch {
      /* sin almacenamiento: el regalo igual funciona */
    }
  },
  remove: (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* sin almacenamiento */
    }
  },
};

/**
 * @param {'add' | 'update'} action
 * @param {string} url
 * @param {Record<string, unknown>} body
 * @param {Array<Record<string, unknown>>} lines
 */
async function mutateCart(action, url, body, lines) {
  const sectionIds = [];
  for (const item of document.querySelectorAll('cart-items-component')) {
    if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: sectionIds.join(','), sections_url: window.location.pathname }),
    });
    const result = await response.json();
    const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: result.sections,
        source: 'cart-gift',
        didError: Boolean(result.status),
      },
    });
    return !result.status;
  } catch (error) {
    deferred.reject(error);
    return false;
  }
}

/**
 * Revisa una meta y, si hace falta, agrega o quita su regalo.
 * @param {HTMLElement} element
 * @returns {Promise<boolean>} true si cambió el carrito
 */
async function syncTier(element) {
  const { variantId, autoKey, cartGiftTier } = element.dataset;
  const shouldHave = element.dataset.shouldHave === 'true';
  const inCart = element.dataset.inCart === 'true';
  const autoPaid = element.dataset.autoPaid === 'true';
  const addedKey = `${ADDED_KEY}-${cartGiftTier}`;
  const declinedKey = `${DECLINED_KEY}-${cartGiftTier}`;

  if (autoKey && (!shouldHave || autoPaid)) {
    // Bajó de la meta, hay otra promoción o el descuento no dejó el regalo en $0.
    if (autoPaid && shouldHave) store.set(declinedKey);
    await mutateCart('update', Theme.routes.cart_change_url, { id: autoKey, quantity: 0 }, [
      { id: autoKey, quantity: 0 },
    ]);
    return true;
  }

  if (!shouldHave) {
    store.remove(addedKey);
    store.remove(declinedKey);
    return false;
  }

  if (inCart || !variantId) return false;

  if (store.get(addedKey)) {
    // El cliente quitó el regalo: no se vuelve a agregar.
    store.set(declinedKey);
    return false;
  }
  if (store.get(declinedKey)) return false;

  const added = await mutateCart(
    'add',
    Theme.routes.cart_add_url,
    { items: [{ id: Number(variantId), quantity: 1, properties: { _regalo: '1' } }] },
    [{ merchandiseId: variantId, quantity: 1 }]
  );
  if (added) store.set(addedKey);
  return true;
}

async function syncGift() {
  if (busy) return;

  const container = document.querySelector('[data-cart-gift]');
  if (!container) return;

  busy = true;
  try {
    for (const element of container.querySelectorAll('[data-cart-gift-tier]')) {
      // Un cambio por vez: el carrito se vuelve a renderizar y la revisión sigue con la meta siguiente.
      if (element instanceof HTMLElement && (await syncTier(element))) break;
    }
  } finally {
    busy = false;
  }
}

/** Espera a que el tema termine de volver a renderizar el carrito. */
function syncAfterRender() {
  setTimeout(syncGift, 400);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = /** @type {CartLinesUpdateEvent} */ (event).promise;
  if (promise) {
    promise.then(syncAfterRender).catch(() => {});
  } else {
    syncAfterRender();
  }
});
document.addEventListener(StandardEvents.cartDiscountUpdate, syncAfterRender);

syncGift();

/**
 * Productos que Shopify dividió en dos líneas por una promoción: el carrito muestra una sola fila
 * (snippets/cart-products.liquid) y aquí se aplica la cantidad escrita como total de todas sus líneas.
 * @param {HTMLElement} row
 * @param {number} quantity
 */
async function setSplitRowQuantity(row, quantity) {
  const mainKey = row.dataset.key;
  if (!mainKey) return;
  const otherKeys = (row.dataset.splitKeys ?? '').split(',').filter(Boolean);

  /** @type {Record<string, number>} */
  const updates = { [mainKey]: Math.max(0, quantity) };
  for (const key of otherKeys) updates[key] = 0;

  const lines = Object.entries(updates).map(([id, lineQuantity]) => ({ id, quantity: lineQuantity }));
  const updated = await mutateCart('update', Theme.routes.cart_update_url ?? '/cart/update.js', { updates }, lines);
  // Si Shopify rechaza el cambio (por ejemplo, por stock), se vuelve a mostrar el carrito real.
  if (!updated) window.location.reload();
}

let splitTimer = 0;

document.addEventListener(
  ThemeEvents.quantitySelectorUpdate,
  (event) => {
    const row = event.target instanceof Element ? event.target.closest('tr[data-split-keys]') : null;
    if (!(row instanceof HTMLElement)) return;

    // La fila agrupa varias líneas: el tema solo cambiaría una de ellas.
    event.stopImmediatePropagation();
    event.stopPropagation();

    const quantity = Number(/** @type {CustomEvent} */ (event).detail?.quantity);
    if (Number.isNaN(quantity)) return;

    window.clearTimeout(splitTimer);
    splitTimer = window.setTimeout(() => setSplitRowQuantity(row, quantity), 300);
  },
  true
);

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-split-remove]') : null;
  const row = button?.closest('tr[data-split-keys]');
  if (!(row instanceof HTMLElement)) return;

  event.preventDefault();
  setSplitRowQuantity(row, 0);
});
